import { useEffect } from "react";
import { usePropertyInventory } from "../../../../../hooks/usePropertyInventory";
import { Button, Card, CardContent, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField, Typography } from "@mui/material";
import { alpha, Box, Grid } from "@mui/system";
import { ArrowForward, Close, CloudUpload } from "@mui/icons-material";
import { CreateApartmentForComplex } from "../../../../../types/propertyInventory/ApartmentComplex";
import { apartmentTypes, statusOptions } from "../../../../../utils/constants";
import { currencies } from "../../../../../utils/constants";
import { Step4Tenants } from "./Step4Tenants";

interface Step3ApartmentDetailsProps {
    complexId: string;
}

export const Step3ApartmentDetails: React.FC<Step3ApartmentDetailsProps> = ({ complexId }) => {
    const { currentStep, setCurrentStep,
        getAllServicesTypes,
        apartmentForComplex,
        setApartmentForComplex,
        servicesTypes,
        getFloorsByComplexId,
        getParkingSpacesByComplexId,
        complexFloors,
        complexParkingSpaces,
        getApartmentComplexById,
        complex,
    } = usePropertyInventory();

    useEffect(() => {
        if (complexId) {
            (async () => {
                await getApartmentComplexById(complexId);
                await getFloorsByComplexId(complexId);
                await getParkingSpacesByComplexId(complexId);
            })();
        }
    }, [complexId, getApartmentComplexById, getFloorsByComplexId, getParkingSpacesByComplexId]);

    const floorOptions = [
        ...(complexFloors || [])
            .slice()
            .sort((a, b) => (a.floorNumber ?? 0) - (b.floorNumber ?? 0))
            .map(floor => ({
                value: floor.id,
                label: `Piso ${floor.floorNumber}`
            }))
    ];

    // Filtrar parqueos disponibles
    const parkingOptions = [
        ...(complexParkingSpaces || [])
            .slice()
            .sort((a, b) => (a.spaceNumber ?? 0) - (b.spaceNumber ?? 0))
            .map(parking => ({
                value: parking.id,
                label: parking.spaceNumber ? `Parqueo ${parking.spaceNumber}` : `Parqueo`
            })),
        { value: '', label: 'Sin parqueo' }
    ];

    const availableParkingCount = parkingOptions.length - 1;

    const handleApartmentMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);

            setApartmentForComplex((prev: CreateApartmentForComplex) =>
                prev && { ...prev, files: [...(prev.files || []), ...files] }
            );
        }
    };

    const removeApartmentMedia = (mediaIndex: number) => {
        setApartmentForComplex((prev: CreateApartmentForComplex) =>
            prev
                && { ...prev, files: prev.files?.filter((_: any, i: number) => i !== mediaIndex) }
        );
    };

    const handleApartmentChange = (
        field: keyof CreateApartmentForComplex,
        value: CreateApartmentForComplex[keyof CreateApartmentForComplex]
    ) => {
        setApartmentForComplex((prev: CreateApartmentForComplex) =>
            prev && { ...prev, [field]: value }
        );
    };

    const handleNext = () => {
        if (apartmentForComplex) setApartmentForComplex(apartmentForComplex);
        setCurrentStep(currentStep + 1);
    };

    const isValidApartment = (apartment: CreateApartmentForComplex) => {
        return apartment.apartmentType && apartment.baseRentAmount && apartment.securityDeposit
            && apartment.apartmentNumber && apartment.assignedFloorId && apartment.bathrooms >= 0 && apartment.squareMeters >= 0
            && apartment.apartmentStatus !== undefined && apartment.currency !== undefined && apartment.files && apartment.files.length > 0
            && (apartment.apartmentServices.every(service => service.ServiceTypeId && service.Provider && service.ServiceAccount));
    };

    const allValid = apartmentForComplex && isValidApartment(apartmentForComplex);

    useEffect(() => {
        (async () => {
            await getAllServicesTypes();
        })();
    }, [getAllServicesTypes]);

    return (
        <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold" color="#2E7D7A" sx={{ mb: 3 }}>
                    Creación de Apartamento para {complex?.name}
                </Typography>

                    <Box>
                        <Typography variant="h6" sx={{ mb: 3, color: '#2E7D7A' }}>
                            Detalles del Apartamento
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Columna izquierda - Datos básicos */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card sx={{ p: 3, mb: 3, backgroundColor: '#FAFAFA' }}>
                                    <Typography variant="h6" sx={{ mb: 2, color: '#2E7D7A' }}>
                                        Información Básica
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        label="Número de Apartamento"
                                        value={apartmentForComplex?.apartmentNumber || ''}
                                        onChange={(e) => handleApartmentChange('apartmentNumber', e.target.value)}
                                        sx={{ mb: 2 }}
                                    />

                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Tipo de Apartamento</InputLabel>
                                        <Select
                                            value={apartmentForComplex?.apartmentType || ''}
                                            onChange={(e) => handleApartmentChange('apartmentType', e.target.value)}
                                            label="Tipo de Apartamento"
                                        >
                                            {apartmentTypes.map(type => (
                                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <FormControl fullWidth>
                                            <InputLabel>Moneda</InputLabel>
                                            <Select
                                                value={apartmentForComplex?.currency || 'CRC'}
                                                onChange={(e) => handleApartmentChange('currency', e.target.value)}
                                                label="Moneda"
                                            >
                                                {currencies.map(currency => (
                                                    <MenuItem key={currency.value} value={currency.value}>
                                                        {currency.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Precio Mensual"
                                                type="number"
                                                value={apartmentForComplex?.baseRentAmount || ''}
                                                helperText={`Moneda debe ser en ${apartmentForComplex?.currency === 1 ? 'USD' : 'CRC'}.`}
                                                onChange={(e) => handleApartmentChange('baseRentAmount', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <FormControl fullWidth>
                                                <InputLabel>Estado</InputLabel>
                                                <Select
                                                    value={apartmentForComplex?.apartmentStatus || 1}
                                                    onChange={(e) => handleApartmentChange('apartmentStatus', e.target.value)}
                                                    label="Estado"
                                                >
                                                    {statusOptions.map(status => (
                                                        <MenuItem key={status.value} value={status.value}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <Box sx={{
                                                                    width: 12,
                                                                    height: 12,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: status.color,
                                                                    mr: 1
                                                                }} />
                                                                {status.label}
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2} sx={{ mt: 2 }}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <FormControl fullWidth>
                                                <InputLabel>Piso</InputLabel>
                                                <Select
                                                    value={apartmentForComplex?.assignedFloorId || ''}
                                                    onChange={(e) => handleApartmentChange('assignedFloorId', e.target.value)}
                                                    label="Piso"
                                                >
                                                    {floorOptions.map(floor => (
                                                        <MenuItem key={floor.value} value={floor.value}>
                                                            {floor.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <FormControl fullWidth disabled={availableParkingCount === 0 && !apartmentForComplex?.assignedParkingSpaceId}>
                                                <InputLabel>Parqueo {availableParkingCount === 0 && '(No disponibles)'}</InputLabel>
                                                <Select
                                                    value={apartmentForComplex?.assignedParkingSpaceId || ''}
                                                    onChange={(e) => handleApartmentChange('assignedParkingSpaceId', e.target.value)}
                                                    label={`Parqueo Asignado ${availableParkingCount === 0 ? '(No disponibles)' : ''}`}
                                                >
                                                    {parkingOptions.map(parking => (
                                                        <MenuItem key={parking.value || 'none'} value={parking.value}>
                                                            {parking.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>


                                    <Grid container spacing={2} sx={{ mt: 2 }}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Baños"
                                                type="number"
                                                value={apartmentForComplex?.bathrooms || ''}
                                                onChange={(e) => handleApartmentChange('bathrooms', parseInt(e.target.value) || 0)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="m²"
                                                type="number"
                                                value={apartmentForComplex?.squareMeters || ''}
                                                onChange={(e) => handleApartmentChange('squareMeters', parseFloat(e.target.value) || 0)}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2} sx={{ mt: 2 }}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={apartmentForComplex?.hasBalcony || false}
                                                        onChange={(e) => handleApartmentChange('hasBalcony', e.target.checked)}
                                                    />
                                                }
                                                label="Tiene Balcón"
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={apartmentForComplex?.hasOwnParking || false}
                                                        onChange={(e) => handleApartmentChange('hasOwnParking', e.target.checked)}
                                                    />
                                                }
                                                label="Parqueo Propio"
                                            />
                                        </Grid>
                                    </Grid>

                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Descripción"
                                        value={apartmentForComplex?.description || ''}
                                        onChange={(e) => handleApartmentChange('description', e.target.value)}
                                        sx={{ mt: 2 }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Depósito de Garantía"
                                        type="number"
                                        value={apartmentForComplex?.securityDeposit || ''}
                                        onChange={(e) => handleApartmentChange('securityDeposit', parseFloat(e.target.value) || 0)}
                                        sx={{ mt: 2 }}
                                    />
                                </Card>

                                {/* Sección de fotos */}
                                <Card sx={{ p: 3, backgroundColor: '#FAFAFA' }}>
                                    <Typography variant="h6" sx={{ mb: 2, color: '#2E7D7A' }}>
                                        Multimedia del Apartamento
                                    </Typography>

                                    {/* Botón de subida */}
                                    <Button
                                        component="label"
                                        fullWidth
                                        sx={{
                                            p: 3,
                                            border: '2px dashed #4DB6AC',
                                            backgroundColor: alpha('#4DB6AC', 0.05),
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: alpha('#4DB6AC', 0.1),
                                            },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textTransform: 'none'
                                        }}
                                    >
                                        <CloudUpload sx={{ fontSize: 40, color: '#4DB6AC', mb: 1 }} />
                                        <Typography variant="body2" color="#2E7D7A">
                                            Subir fotos y videos del apartamento
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Imágenes: JPG, PNG, WebP | Videos: MP4, MOV (Máx. 10MB)
                                        </Typography>
                                        <input
                                            type="file"
                                            hidden
                                            multiple
                                            accept="image/*,video/*"
                                            onChange={(e) => handleApartmentMediaUpload(e)}
                                        />
                                    </Button>

                                    {/* Preview de Imágenes */}
                                    {apartmentForComplex?.files && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Archivos ({apartmentForComplex.files?.length})
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                {apartmentForComplex.files?.map((img: File, imgIndex: number) => (
                                                    <Box
                                                        key={imgIndex}
                                                        sx={{
                                                            position: 'relative',
                                                            width: 100,
                                                            height: 100,
                                                            borderRadius: 1,
                                                            overflow: 'hidden',
                                                            border: '2px solid #E0E0E0',
                                                            '&:hover .delete-overlay': {
                                                                opacity: 1
                                                            }
                                                        }}
                                                    >
                                                        <img
                                                            src={URL.createObjectURL(img)}
                                                            alt={`Preview ${imgIndex + 1}`}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                        <Box
                                                            className="delete-overlay"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => removeApartmentMedia(imgIndex)}
                                                        >
                                                            <Close sx={{ color: 'white', fontSize: 28 }} />
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Card>
                            </Grid>

                            {/* Columna derecha - Servicios */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card sx={{ p: 3, backgroundColor: '#FAFAFA' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" sx={{ color: '#2E7D7A' }}>
                                            Servicios del Apartamento
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                handleApartmentChange(
                                                    'apartmentServices',
                                                    [...(apartmentForComplex?.apartmentServices || []), {
                                                        ServiceTypeId: undefined,
                                                        Provider: '',
                                                        ServiceAccount: '',
                                                        IsIncludedInRent: false
                                                    }]
                                                );
                                            }}
                                            sx={{ borderColor: '#4DB6AC', color: '#4DB6AC' }}
                                        >
                                            + Añadir Servicio
                                        </Button>
                                    </Box>

                                    {apartmentForComplex?.apartmentServices?.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                            No hay servicios agregados. Haz clic en "Añadir Servicio" para comenzar.
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {apartmentForComplex?.apartmentServices?.map((service, serviceIndex) => (
                                                <Card key={serviceIndex} sx={{ p: 2, backgroundColor: 'white' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                        <Typography variant="subtitle2" color="#2E7D7A">
                                                            Servicio {serviceIndex + 1}
                                                        </Typography>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                const updatedServices = apartmentForComplex?.apartmentServices?.filter((_, idx) => idx !== serviceIndex);
                                                                handleApartmentChange('apartmentServices', updatedServices);
                                                            }}
                                                        >
                                                            Eliminar
                                                        </Button>
                                                    </Box>

                                                    <Grid container spacing={2}>
                                                        <Grid size={{ xs: 12 }}>
                                                            <FormControl fullWidth size="small">
                                                                <InputLabel>Tipo de Servicio</InputLabel>
                                                                <Select
                                                                    value={service.ServiceTypeId || 0}
                                                                    onChange={(e) => {
                                                                        const updatedServices = [...apartmentForComplex.apartmentServices];
                                                                        updatedServices[serviceIndex].ServiceTypeId = Number(e.target.value);
                                                                        handleApartmentChange('apartmentServices', updatedServices);
                                                                    }}
                                                                    label="Tipo de Servicio"
                                                                >
                                                                    {servicesTypes.filter(st => st.isUtility).map((type) => (
                                                                        <MenuItem key={type.id} value={type.id}>
                                                                            {type.serviceName}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>

                                                        <Grid size={{ xs: 12, md: 6 }}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                label="Proveedor"
                                                                value={service.Provider || ''}
                                                                onChange={(e) => {
                                                                    const updatedServices = [...apartmentForComplex.apartmentServices];
                                                                    updatedServices[serviceIndex].Provider = e.target.value;
                                                                    handleApartmentChange('apartmentServices', updatedServices);
                                                                }}
                                                            />
                                                        </Grid>

                                                        <Grid size={{ xs: 12, md: 6 }}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                label="Número de Cuenta"
                                                                value={service.ServiceAccount || ''}
                                                                onChange={(e) => {
                                                                    const updatedServices = [...apartmentForComplex.apartmentServices];
                                                                    updatedServices[serviceIndex].ServiceAccount = e.target.value;
                                                                    handleApartmentChange('apartmentServices', updatedServices);
                                                                }}
                                                            />
                                                        </Grid>

                                                        <Grid size={{ xs: 12 }}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={service.IsIncludedInRent || false}
                                                                        onChange={(e) => {
                                                                            const updatedServices = [...apartmentForComplex.apartmentServices];
                                                                            updatedServices[serviceIndex].IsIncludedInRent = e.target.checked;
                                                                            handleApartmentChange('apartmentServices', updatedServices);
                                                                        }}
                                                                    />
                                                                }
                                                                label="Incluido en la renta"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Card>
                                            ))}
                                        </Box>
                                    )}
                                </Card>
                                <Step4Tenants />
                            </Grid>
                        </Grid>
                    </Box>
                {/* Botones de navegación */}
                <Box sx={{ display: 'flex', justifyContent: 'end', mt: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            endIcon={<ArrowForward />}
                            onClick={handleNext}
                            disabled={!allValid}
                            sx={{
                                backgroundColor: allValid ? '#4DB6AC' : '#E0E0E0',
                                color: allValid ? 'white' : '#999',
                                '&:hover': {
                                    backgroundColor: allValid ? '#26A69A' : '#E0E0E0',
                                }
                            }}
                        >
                            Siguiente: Resumen Final
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card >
    );
};